/**
 * The canonical stored form (decision 66; part 04 log, principles L1–L6 and tables E, B, I): a chapter
 * written from its model, so every source with the same model stores the same text, the text typing it
 * would store. Canonicalising is idempotent and keeps what the writer sees and every construct (N11 aside).
 *
 * Blocks are separated by one blank line (none inside a tight list item), every line carries its
 * container's prefix, and indentation appears only where it puts a block inside a list item or a note.
 * Delimiters of emphasis and small caps are kept as written; text around them is escaped so it stays text
 * wherever it lands (table E).
 */
import { footnoteLabelEnd } from '../parser/footnotes.ts';
import { parseChapter } from '../parser/parse.ts';
import { holdsDelimiter } from './inline.ts';
import type { Inline, Link, TextRun } from './inline.ts';
import { chapterModel, endsOpen } from './model.ts';
import type { Block, ChapterModel, List } from './model.ts';

/** The largest number a list marker can hold (CommonMark 5.2: nine digits). */
const MAX_ITEM_NUMBER = 999_999_999;

/**
 * Thrown when a chapter's canonical form would be longer than its bound (part 04 log, table OB): an imported
 * source written so that the canonical form repeats it (a long link reference definition used many times, lazy
 * lines deep in containers). The import review shows it; nothing is stored.
 */
export class OutputLimitError extends RangeError {
  override readonly name = 'OutputLimitError';
  /** The bound that was passed, in UTF-16 code units. */
  readonly limit: number;

  constructor(limit: number) {
    super(`the canonical form would be longer than ${limit} characters`);
    this.limit = limit;
  }
}

/** The longest canonical form a source of `length` code units may have (table OB): four times it, plus 1 Mi. */
export function outputLimit(length: number): number {
  return 4 * length + 1_048_576;
}

/**
 * What is left of the output bound while a chapter is written (table OB). Every character of the output is
 * counted once, where it is added: a leaf's lines as it writes them, each newline with its line, and each
 * prefix, marker or blank line as the container adding it writes it. Writing stops as soon as the count passes.
 * Exported for its tests.
 */
export class Budget {
  private readonly limit: number;
  private left: number;

  constructor(limit: number) {
    this.limit = limit;
    this.left = limit;
  }

  /** Counts `n` more characters of output. */
  spend(n: number): void {
    this.left -= n;
    if (this.left < 0) throw new OutputLimitError(this.limit);
  }

  /** Throws when a line that has grown to `n` characters can't fit, before it is counted. */
  fits(n: number): void {
    if (n > this.left) throw new OutputLimitError(this.limit);
  }
}

/** No bound: for text whose size its caller already holds (`escapeLiteral`). */
const UNBOUNDED = (): Budget => new Budget(Number.POSITIVE_INFINITY);

/** What a list's marker character is, for the lists written next to it. */
type Marker = '-' | '*' | '.' | ')';

/** What every token says about its edges, for the text beside it. */
interface Edges {
  /** A delimiter (a mark, or a text run's delimiter piece) starts it. */
  readonly delimiterStart: boolean;
  /** A delimiter ends it. */
  readonly delimiterEnd: boolean;
  /** `link-open` for a link's or kept brackets' `[`; `label-close` for a `]` a `(` or `:` could extend. */
  readonly role: 'link-open' | 'label-close' | null;
}

/**
 * A piece of a written line: text to escape, markup as written, a line break, or the edge of the content (the
 * list starts and ends with one, so every token has neighbours).
 */
type Token = Edges &
  (
    | { readonly type: 'text'; readonly run: TextRun; readonly inLink: boolean }
    | {
        readonly type: 'mark';
        readonly text: string;
        /** On a link, image or bracket opening: the first thing in its text, which N11 may write plain. */
        readonly lead?: Inline;
      }
    | { readonly type: 'break'; readonly style: 'soft' | 'backslash' | 'spaces' }
    | { readonly type: 'edge' }
  );

const EDGE: Token = { type: 'edge', delimiterStart: false, delimiterEnd: false, role: null };

/** A token of the content itself, not an edge. */
type Content = Exclude<Token, { readonly type: 'edge' }>;

/** Characters escaped wherever they are (E1–E8). */
const ALWAYS = new Set(['\\', '*', '_', '^', '[', '<', '&', '`']);
/** Characters escaped at a line start (E13–E18). */
const LINE_START = new Set(['#', '>', '-', '+', '=', '~']);
/** A line start that reads as an ordered list marker (E19): digits, then the delimiter to escape. */
const ORDERED_MARKER = /^\d+(?=[.)](?:[ \t]|$))/u;

/** A numeric character reference for the code point at the start of `char`. */
function reference(char: string): string {
  return `&#${Number(char.codePointAt(0))};`;
}

/** Where a text run sits, as far as its escapes care. */
export interface RunContext {
  /** Its first character starts a line (block constructs open there). */
  readonly lineStart: boolean;
  /** Its first character would lose leading whitespace (a line or content start). */
  readonly leadingEdge: boolean;
  /** Its last character would lose trailing whitespace (a line or content end). */
  readonly trailingEdge: boolean;
  readonly inLink: boolean;
  readonly afterDelimiter: boolean;
  readonly beforeDelimiter: boolean;
  readonly beforeLink: boolean;
  /** It follows a footnote reference or kept brackets, whose `]` a `(` or `:` would extend. */
  readonly afterLabel: boolean;
  /** Its last character ends an ATX heading's content. */
  readonly headingEnd: boolean;
}

/** One character of a text run, escaped for where it sits (table E); `at` is its offset in the run. */
function escapeCharacter(char: string, at: number, length: number, cx: RunContext, digits: number): string {
  const first = at === 0;
  const final = at + char.length === length;
  if (char === '\n' || char === '\r') return reference(char);
  if ((char === ' ' || char === '\t') && ((first && cx.leadingEdge) || (final && cx.trailingEdge))) return reference(char);
  if (ALWAYS.has(char)) return `\\${char}`;
  if (char === ']' && cx.inLink) return '\\]';
  if (char === '!' && final && cx.beforeLink) return '\\!';
  if ((char === '(' || char === ':') && first && cx.afterLabel) return `\\${char}`;
  if (LINE_START.has(char) && first && cx.lineStart) return `\\${char}`;
  if (at === digits) return `\\${char}`;
  if (char === '#' && final && cx.headingEnd) return '\\#';
  return char;
}

/** A text run's length in characters of its source. */
function runLength(run: TextRun): number {
  return run.pieces.reduce((sum, p) => sum + p.text.length, 0);
}

/**
 * Where E19's delimiter falls in the text runs a line starts with, `list[from]` on: the runs are read as one,
 * as they are written (adjacent runs join into one when read back: brackets written as text, or emphasis N12
 * wrote plain), so the delimiter may be in a later run and what follows it may be too. -1 when none.
 */
function orderedMarker(list: readonly Content[], from: number): number {
  let text = '';
  for (let i = from; i < list.length; i++) {
    const token = list[i];
    if (token?.type !== 'text') break;
    text += token.run.pieces.map((p) => p.text).join('');
  }
  return ORDERED_MARKER.exec(text)?.[0].length ?? -1;
}

/**
 * One text run, escaped for where it sits (table E). Delimiter pieces stay as written. A character from a
 * reference that touches a delimiter stays a reference (E23): as a character it could change the
 * delimiter's flanking, which only the class of its neighbours decides. `digits` is the offset of E19's
 * delimiter in the run, or -1.
 */
export function escapeRun(run: TextRun, cx: RunContext, digits: number): string {
  const { pieces } = run;
  const length = runLength(run);
  let out = '';
  let at = 0;
  pieces.forEach((piece, k) => {
    const { text } = piece;
    if (piece.origin === 'delimiter') {
      out += text;
      at += text.length;
      return;
    }
    const touchesBefore = (k === 0 && cx.afterDelimiter) || pieces[k - 1]?.origin === 'delimiter';
    const touchesAfter = (k === pieces.length - 1 && cx.beforeDelimiter) || pieces[k + 1]?.origin === 'delimiter';
    // Fix-next 51: only a piece's first and last character, E19's delimiter and the characters in INNER can be written
    // as anything but themselves, so the units between them are copied whole rather than read a character at a time.
    const last = text.length - lastCharLength(text);
    /** The character starting at `j`, escaped (`j` is 0, `last`, E19's delimiter or an INNER character). */
    const one = (j: number): number => {
      const char = String.fromCodePoint(Number(text.codePointAt(j)));
      const touches = (j === 0 && touchesBefore) || (j === last && touchesAfter);
      out += piece.origin === 'entity' && touches ? reference(char) : escapeCharacter(char, at + j, length, cx, digits);
      return j + char.length;
    };
    const marker = digits - at;
    let j = text.length === 0 ? 0 : one(0);
    INNER.lastIndex = j;
    while (j < text.length) {
      const special = INNER.test(text) ? INNER.lastIndex - 1 : text.length;
      const stop = Math.min(special, marker >= j && !midPair(text, marker) ? marker : text.length, last);
      out += text.slice(j, stop);
      j = one(stop);
      INNER.lastIndex = j;
    }
    at += text.length;
  });
  return out;
}

/**
 * The characters `escapeCharacter` may write as something else away from a run's first and last character and E19's
 * delimiter (table E): line ends, ALWAYS, and `]` in link text. Every one is a single UTF-16 unit.
 */
const INNER = /[\n\r\\*_^[<&`\]]/gu;

/** How many units the last character of `text` takes: 2 for a surrogate pair (1 for an empty text: no unit at -1). */
function lastCharLength(text: string): number {
  return midPair(text, text.length - 1) ? 2 : 1;
}

/** Whether offset `k` of `text` falls between the halves of a surrogate pair. */
function midPair(text: string, k: number): boolean {
  const high = text.charCodeAt(k - 1);
  const low = text.charCodeAt(k);
  return high >= 0xd800 && high <= 0xdbff && low >= 0xdc00 && low <= 0xdfff;
}

/**
 * A link destination: bare when it can be, else in angle brackets (I4). Lezer reads no escapes between angle
 * brackets, so `<` and `>` there are character references; control characters are references everywhere.
 */
function destination(value: string): string {
  const angle = value === '' || value.includes(' ');
  const escaped = Array.from(value)
    .map((char) => {
      const code = Number(char.codePointAt(0));
      if (code < 0x20 || code === 0x7f || (angle && (char === '<' || char === '>'))) return reference(char);
      return (angle ? '\\&' : '\\()<>&').includes(char) ? `\\${char}` : char;
    })
    .join('');
  return angle ? `<${escaped}>` : escaped;
}

/** A link title in double quotes (I3). */
function title(value: string): string {
  return `"${value.replace(/[\\"&]/gu, '\\$&')}"`;
}

/**
 * A link's or image's closing, `](destination "title")`, by destination and title. N1 writes a definition's target
 * at every use, so each is escaped once per inline content and shared (table OB): escaping it again at every use
 * would cost uses × its length before the output bound could see a character of it.
 */
type Closings = Map<string, Map<string | null, string>>;

function closing(item: Link, closings: Closings): string {
  let byTitle = closings.get(item.destination);
  if (byTitle === undefined) {
    byTitle = new Map();
    closings.set(item.destination, byTitle);
  }
  let text = byTitle.get(item.title);
  if (text === undefined) {
    text = `](${destination(item.destination)}${item.title === null ? '' : ` ${title(item.title)}`})`;
    byTitle.set(item.title, text);
  }
  return text;
}

/** A mark token. */
function mark(text: string, delimiter: boolean, role: Edges['role']): Content {
  return { type: 'mark', text, delimiterStart: delimiter, delimiterEnd: delimiter, role };
}

/**
 * Where a lead N11 writes plain ended: its `^^` closing would have stood before `list[at]`, so the delimiters on
 * either side now meet other neighbours (table N11b).
 */
interface Seam {
  /** The link, image or kept brackets whose text the lead opens. */
  readonly owner: Inline;
  /** The index in the token list of the first token after the lead's content. */
  readonly at: number;
}

/**
 * What writing one inline content decides as it goes: the leads N11 writes plain, the texts table N11b writes
 * without formatting, the seams those leads leave, and the shared closings.
 */
interface Writing {
  readonly plain: ReadonlySet<Inline>;
  readonly flat: ReadonlySet<Inline>;
  readonly seams: Seam[];
  readonly closings: Closings;
}

/** The opening mark of a link, image or kept brackets, with the first thing in its text as its lead. */
function opening(text: string, role: Edges['role'], children: readonly Inline[], plain: ReadonlySet<Inline>): Content {
  const [first] = children;
  const lead = first === undefined || plain.has(first) ? {} : { lead: first };
  return { type: 'mark', text, delimiterStart: false, delimiterEnd: false, role, ...lead };
}

/**
 * The text of a link, image or kept brackets (`content`: their children, or a pair's whole text when N11 writes it
 * as text). A lead written plain leaves a seam where its closing would have stood (table N11b); the lead is the
 * only plain span in it, since only small caps opening the text are made plain.
 */
function linkText(owner: Inline, content: readonly Inline[], inLink: boolean, out: Content[], w: Writing): void {
  const lead = content.findIndex((item) => w.plain.has(item));
  if (lead === -1) {
    tokens(content, inLink, out, w);
    return;
  }
  tokens(content.slice(0, lead + 1), inLink, out, w);
  w.seams.push({ owner, at: out.length });
  tokens(content.slice(lead + 1), inLink, out, w);
}

/**
 * Content with its emphasis and small caps written plain and its delimiter pieces as ordinary text (N12):
 * brackets written as text no longer keep Lezer from pairing what they held with what lies outside, so what
 * they held can't be delimiters. Links and images keep their own content.
 */
function withoutFormatting(content: readonly Inline[]): Inline[] {
  return content.flatMap((item): Inline[] => {
    if (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps') return withoutFormatting(item.children);
    if (item.kind === 'text') return [{ kind: 'text', pieces: item.pieces.map((p) => (p.origin === 'delimiter' ? { ...p, origin: 'literal' } : p)) }];
    return [item];
  });
}

/** Emphasis, strong emphasis or small caps as marks around their content. */
function span(open: string, children: readonly Inline[], close: string, inLink: boolean, out: Content[], w: Writing): void {
  out.push(mark(open, true, null));
  tokens(children, inLink, out, w);
  out.push(mark(close, true, null));
}

/** The inline elements as a flat token list, links and spans opened and closed by marks. */
function tokens(content: readonly Inline[], inLink: boolean, out: Content[], w: Writing): Content[] {
  for (const [index, item] of content.entries()) {
    switch (item.kind) {
      case 'text':
        out.push({
          type: 'text',
          run: item,
          inLink,
          // Text follows text where N11 wrote a lead plain (the seam, table N11b) or brackets as text: a delimiter
          // starting it is one an entity before it touches (E23).
          delimiterStart: item.pieces.findIndex((p) => p.origin === 'delimiter') === 0,
          delimiterEnd: item.pieces.findLastIndex((p) => p.origin === 'delimiter') === item.pieces.length - 1,
          role: null,
        });
        break;
      case 'soft-break':
        out.push({ ...EDGE, type: 'break', style: 'soft' });
        break;
      case 'hard-break':
        out.push({ ...EDGE, type: 'break', style: item.spaces ? 'spaces' : 'backslash' });
        break;
      case 'small-caps':
        if (w.plain.has(item)) tokens(item.children, inLink, out, w);
        else span(item.open, item.children, item.close, inLink, out, w);
        break;
      case 'emphasis':
      case 'strong':
        span(item.open, item.children, item.close, inLink, out, w);
        break;
      case 'link':
      case 'image': {
        const image = item.kind === 'image';
        out.push(opening(image ? '![' : '[', image ? null : 'link-open', item.children, w.plain));
        linkText(item, w.flat.has(item) ? withoutFormatting(item.children) : item.children, true, out, w);
        out.push(mark(closing(item, w.closings), false, null));
        break;
      }
      case 'brackets': {
        // N12: a label-less pair right before a link would take the link's text as its label.
        const following = content[index + 1];
        const captures = item.close === ']' && (following?.kind === 'link' || (following?.kind === 'brackets' && following.open === '['));
        if (captures || w.flat.has(item)) {
          // N12, or table N11b: brackets written as text no longer scope what they hold, so it is written plain.
          tokens(withoutFormatting(item.text), inLink, out, w);
        } else if (holdsDelimiter(item.children, w.plain)) {
          out.push(opening(item.open, item.open === '[' ? 'link-open' : null, item.children, w.plain));
          linkText(item, item.children, true, out, w);
          out.push(mark(item.close, false, 'label-close'));
        } else {
          // N11 wrote the span that kept these brackets without its marks: they are text now, as they read.
          linkText(item, item.text, inLink, out, w);
        }
        break;
      }
      case 'footnote-reference':
        out.push(mark(`[^${item.label}]`, false, 'label-close'));
    }
  }
  return out;
}

/**
 * Inline content as lines, escaped (table E). `lineStart` says its first character starts a line (a
 * paragraph's does; a heading's or centered block's follows its mark); `heading` marks an ATX heading, whose
 * end sheds `#`.
 *
 * N11: link text opening with small caps writes as `[^^`, which the grammar reads as a footnote reference
 * when no whitespace or bracket comes before the first `]` (part 03). Only small caps can put a `^` right
 * after a link's `[` (text escapes every `^`). Such a span is written without its marks: the text stays, its
 * small caps go, since no other writing keeps both.
 *
 * Table N11b: the closing `^^` that goes was punctuation to the delimiters on either side of it. Where one now
 * meets a character of another class, or a delimiter run of its own character it joins, it would read otherwise,
 * so that link's whole text is written without formatting, as N12 writes brackets.
 */
function inlineLines(content: readonly Inline[], lineStart: boolean, heading: boolean, budget: Budget): string[] {
  const { lines, length } = settledLines(content, lineStart, heading, budget);
  budget.spend(length + 1);
  return lines;
}

/**
 * Inline content as lines once N11 and table N11b have settled, and the length of their text; not yet counted in
 * `budget` (see {@link inlineLines}).
 */
function settledLines(content: readonly Inline[], lineStart: boolean, heading: boolean, budget: Budget): { lines: string[]; length: number } {
  const plain = new Set<Inline>();
  const flat = new Set<Inline>();
  const closings: Closings = new Map();
  for (;;) {
    const w: Writing = { plain, flat, seams: [], closings };
    const list = tokens(content, false, [], w);
    const { lines, leads, seams } = writeInline(list, w.seams, lineStart, heading, budget);
    const text = lines.join('\n');
    // A lead reads as a reference by its own link's text alone, and writing one plain can't make another (small
    // caps can't open with small caps), so every one found is resolved at once. A text written without formatting
    // holds no lead or seam and changes nothing outside its link, so at most three passes are made.
    const found = leads.filter(({ at }) => footnoteLabelEnd(text, at) !== -1);
    const shifted = seams.filter(({ seam, offset }) => shifts(text, offset, list, seam.at));
    if (found.length === 0 && shifted.length === 0) return { lines, length: text.length };
    for (const { lead } of found) plain.add(lead);
    for (const { seam } of shifted) flat.add(seam.owner);
  }
}

/** Lezer's punctuation, which emphasis flanking reads (one UTF-16 unit, as Lezer looks). */
const PUNCTUATION = /[\p{S}\p{P}]/u;

/**
 * Table N11b: whether a delimiter beside the seam at `offset` in `text` reads otherwise now that the `^^` between
 * (punctuation) is gone. `list[at]` is the first token after the seam, `list[at - 1]` the last before it.
 */
function shifts(text: string, offset: number, list: readonly Content[], at: number): boolean {
  const before = text.slice(offset - 1, offset);
  const after = text.slice(offset, offset + 1);
  const left = list[at - 1]?.delimiterEnd === true;
  const right = list[at]?.delimiterStart === true;
  if (left && right && before === after) return true;
  return (left && !PUNCTUATION.test(after)) || (right && !PUNCTUATION.test(before));
}

/**
 * Tokens as lines, and where each link opening's `[` is in the written text. Stops once the text can't fit in
 * `budget`; the caller counts it.
 */
function writeInline(
  list: readonly Content[],
  seams: readonly Seam[],
  lineStart: boolean,
  heading: boolean,
  budget: Budget,
): { lines: string[]; leads: { lead: Inline; at: number }[]; seams: { seam: Seam; offset: number }[] } {
  const lines: string[] = [];
  const leads: { lead: Inline; at: number }[] = [];
  const placed: { seam: Seam; offset: number }[] = [];
  let seamAt = 0;
  let written = 0;
  let line = '';
  // E19's delimiter, as an offset from the start of the current text run (orderedMarker); -1 when none.
  let marker = -1;
  for (const [i, token] of list.entries()) {
    for (let seam = seams[seamAt]; seam?.at === i; seam = seams[++seamAt]) placed.push({ seam, offset: written + line.length });
    const previous = list[i - 1] ?? EDGE;
    const next = list[i + 1] ?? EDGE;
    if (token.type === 'break') {
      const done = line + (token.style === 'soft' ? '' : hardBreak(token.style, previous));
      lines.push(done);
      written += done.length + 1;
      line = '';
    } else if (token.type === 'mark') {
      if (token.lead !== undefined) leads.push({ lead: token.lead, at: written + line.length + token.text.length - 1 });
      line += token.text;
    } else {
      const atLineStart = previous.type === 'break' || (previous.type === 'edge' && lineStart);
      if (atLineStart) marker = orderedMarker(list, i);
      line += escapeRun(token.run, {
        lineStart: atLineStart,
        leadingEdge: previous.type === 'edge' || previous.type === 'break',
        trailingEdge: next.type === 'edge' || (next.type === 'break' && next.style === 'soft'),
        inLink: token.inLink,
        afterDelimiter: previous.delimiterEnd,
        beforeDelimiter: next.delimiterStart,
        beforeLink: next.role === 'link-open',
        afterLabel: previous.role === 'label-close',
        headingEnd: heading && next.type === 'edge',
      }, marker);
      marker = marker < 0 ? -1 : marker - runLength(token.run);
    }
    if (token.type !== 'text') marker = -1;
    budget.fits(written + line.length);
  }
  lines.push(line);
  return { lines, leads, seams: placed };
}

/**
 * How a hard break is written (N5): `\\`, or two spaces when the source had spaces and the break follows a
 * delimiter, whose flanking the next character decides (a space is whitespace, `\\` punctuation).
 */
function hardBreak(style: 'backslash' | 'spaces', previous: Token): string {
  return style === 'spaces' && previous.delimiterEnd ? '  ' : '\\';
}

/** How an empty paragraph's no-break space is stored (part 10b, S1). */
const EMPTY_MARK = '&nbsp;';

/** S1: how many no-break spaces an empty paragraph holds, or 0 for any other content. */
function emptyCount(content: readonly Inline[]): number {
  let count = 0;
  for (const item of content) {
    if (item.kind !== 'text') return 0;
    for (const piece of item.pieces) {
      if (!/^\u00a0+$/u.test(piece.text)) return 0;
      count += piece.text.length;
    }
  }
  return count;
}

/**
 * Part 10b, S1: whether a paragraph's content is no-break spaces and nothing else, an empty paragraph: a writer's blank
 * line between paragraphs, which Enter makes and every reader shows as a line.
 */
export function isEmptyParagraph(content: readonly Inline[]): boolean {
  return emptyCount(content) > 0;
}

/** S1: an empty paragraph's line, each no-break space as `&nbsp;`; counted before it is built. */
function emptyLine(count: number, budget: Budget): string[] {
  budget.spend(count * EMPTY_MARK.length + 1);
  return [EMPTY_MARK.repeat(count)];
}

/** Adds a blank line to `out`, counting its newline. */
function blank(out: string[], budget: Budget): void {
  budget.spend(1);
  out.push('');
}

/** Appends `items` to `out` one by one: spreading a long list into `push` overflows the stack (deep-fix r2). */
function appendAll(out: string[], items: readonly string[]): void {
  for (const item of items) out.push(item);
}

/** `lines` indented by `width` spaces, counted before they are written; blank lines stay empty. */
function indent(lines: readonly string[], width: number, budget: Budget): string[] {
  budget.spend(width * lines.filter((line) => line !== '').length);
  const pad = ' '.repeat(width);
  return lines.map((line) => (line === '' ? '' : pad + line));
}

/** The marker character for a list next to `neighbour` (N3, B11): adjacent lists of one kind alternate. */
function markerFor(list: List, neighbour: Marker | null): Marker {
  if (list.start === null) return neighbour === '-' ? '*' : '-';
  return neighbour === '.' ? ')' : '.';
}

/** A list's lines (B9–B13). `close` passes on to its last item's last block. */
function listLines(list: List, marker: Marker, close: boolean, budget: Budget): string[] {
  const out: string[] = [];
  list.items.forEach((blocks, k) => {
    if (k > 0 && list.loose) blank(out, budget);
    const number = list.start === null ? '' : String(Math.min(list.start + k, MAX_ITEM_NUMBER));
    const head = number + marker;
    const [first, ...rest] = blockLines(blocks, !list.loose, marker, close && k === list.items.length - 1, budget);
    // The marker and its space: a line of its own, or ahead of the first line.
    budget.spend(head.length + 1);
    // B13: `* ***` reads as a scene break, so that item's break goes on the next line. Nothing else can make
    // an item's first line one: text escapes `-` and `*` at a line start, and a list first in an item takes
    // the other bullet (N3), so `- * -` never runs to three of one kind.
    const breakFirst = marker === '*' && blocks.findIndex((b) => b.kind === 'scene-break') === 0;
    if (first === undefined) out.push(head);
    else if (breakFirst) appendAll(out, [head, ...indent([first, ...rest], head.length + 1, budget)]);
    else appendAll(out, [`${head} ${first}`, ...indent(rest, head.length + 1, budget)]);
  });
  return out;
}

/**
 * One block's lines. `neighbour` is the marker of the list just before; `close` says a block follows with no
 * blank line between (a tight list item), so no lazy line may join its last paragraph: a quote ending in one
 * gets a blank quote line (B18), as the only tight way to end it.
 */
function blockText(block: Block, neighbour: Marker | null, close: boolean, budget: Budget): { lines: string[]; marker: Marker | null } {
  switch (block.kind) {
    case 'paragraph': {
      const empty = emptyCount(block.content);
      return { lines: empty > 0 ? emptyLine(empty, budget) : inlineLines(block.content, true, false, budget), marker: null };
    }
    case 'heading': {
      // Only a setext heading (level 1 or 2) can hold a line break: one in the content as written, so a bracket pair
      // N11 or table N11b writes as text brings its label's breaks, which kept brackets write as spaces. N11 and N11b
      // decide alike as either heading: no escape a line start or a heading's end adds is beside a lead or a seam.
      const atx = settledLines(block.content, false, true, budget);
      if (atx.lines.length > 1) {
        const lines = inlineLines(block.content, true, false, budget);
        budget.spend(4);
        return { lines: [...lines, block.level === 1 ? '===' : '---'], marker: null };
      }
      budget.spend(atx.length + 1);
      const text = atx.lines.join('');
      const marks = '#'.repeat(block.level);
      budget.spend(text === '' ? marks.length : marks.length + 1);
      return { lines: [text === '' ? marks : `${marks} ${text}`], marker: null };
    }
    case 'centered': {
      const lines = inlineLines(block.content, false, false, budget);
      const last = lines.length - 1;
      budget.spend(4);
      return { lines: lines.map((line, i) => `${i === 0 ? '->' : ''}${line}${i === last ? '<-' : ''}`), marker: null };
    }
    case 'scene-break':
      budget.spend(4);
      return { lines: ['***'], marker: null };
    case 'quote': {
      const inner = blockLines(block.blocks, false, null, false, budget);
      if (close && endsOpen(block, true)) blank(inner, budget);
      // `> ` ahead of every line, `>` alone on a blank one or for an empty quote.
      const blanks = inner.filter((line) => line === '').length;
      budget.spend(inner.length === 0 ? 2 : 2 * inner.length - blanks);
      return { lines: inner.length === 0 ? ['>'] : inner.map((line) => (line === '' ? '>' : `> ${line}`)), marker: null };
    }
    case 'list': {
      const marker = markerFor(block, neighbour);
      return { lines: listLines(block, marker, close, budget), marker };
    }
    case 'footnote': {
      const [first, ...rest] = blockLines(block.blocks, false, null, close, budget);
      const head = `[^${block.label}]:`;
      // The label and its space: a line of its own, or ahead of the first line.
      budget.spend(head.length + 1);
      if (first === undefined) return { lines: [head], marker: null };
      // A container's later lines line up with its first (a list item's content column), and a note's later
      // lines are indented four whatever its label's width, so only a leaf block shares the label's line (B14).
      const container = block.blocks.findIndex((b) => b.kind === 'quote' || b.kind === 'list' || b.kind === 'footnote') === 0;
      return { lines: container ? [head, ...indent([first, ...rest], 4, budget)] : [`${head} ${first}`, ...indent(rest, 4, budget)], marker: null };
    }
  }
}

/**
 * Blocks as lines, a blank line between them unless `tight` (N8). `parentMarker` is the marker of the list
 * item holding them: a list that is the item's first block alternates with it, so `- * -` never reads as a
 * scene break. `closeEnd` closes the last block (see {@link blockText}), for a container that is closed.
 */
function blockLines(blocks: readonly Block[], tight: boolean, parentMarker: Marker | null, closeEnd: boolean, budget: Budget): string[] {
  const out: string[] = [];
  let neighbour = parentMarker;
  for (const [i, block] of blocks.entries()) {
    const last = i === blocks.length - 1;
    const { lines, marker } = blockText(block, neighbour, last ? closeEnd : tight, budget);
    if (i > 0 && !tight) blank(out, budget);
    appendAll(out, lines);
    neighbour = marker;
  }
  return out;
}

/**
 * The canonical text of a chapter model: its lines, each ended by a newline. Throws an OutputLimitError, having
 * written no more than about `limit` characters, when the text would be longer (table OB).
 */
export function serializeChapter(model: ChapterModel, limit = Number.POSITIVE_INFINITY): string {
  const lines = blockLines(model.blocks, false, null, false, new Budget(limit));
  return lines.length === 0 ? '' : `${lines.join('\n')}\n`;
}

/**
 * `source` as the canonical form reads it: every CRLF and lone CR a LF (N10), since CommonMark and the editor read a
 * lone CR as a line end where Lezer reads it as text; and every U+0000 a U+FFFD (N14, deep-fix r7), as CommonMark
 * 2.3 reads it, since no writing reads back as U+0000 (`&#0;` is U+FFFD). The stored form holds neither.
 */
export function normalizeSource(source: string): string {
  return source.replace(/\r\n?/gu, '\n').replaceAll('\u0000', '\uFFFD');
}

/**
 * The canonical stored form of a chapter's source (decision 66): the model of its text, normalised (N10, N14),
 * written out. Idempotent; keeps the visible text and the model (N11 aside). Throws an
 * OutputLimitError when that would be longer than `outputLimit(source.length)` (table OB, import bombs).
 */
export function canonicalize(source: string): string {
  return serializeChapter(chapterModel(parseChapter(normalizeSource(source))), outputLimit(source.length));
}

/** Whether `source` is already in canonical form. */
export function isCanonical(source: string): boolean {
  return canonicalize(source) === source;
}

/**
 * The stored form of `text` as a paragraph of its own, all of it literal (decision 66: typed, pasted or
 * imported prose). Line breaks in `text` are kept as characters; callers split paragraphs and lines first.
 */
export function escapeLiteral(text: string): string {
  const run: TextRun = { kind: 'text', pieces: [{ text, from: 0, to: text.length, origin: 'literal' }] };
  return inlineLines([run], true, false, UNBOUNDED()).join('');
}
